import classnames from 'classnames/bind'
import { useRouter } from 'next/router'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslate } from 'react-polyglot'
import { GlobalThemeColors } from '~/@types/colors'
import { GlobalTextPreset } from '~/@types/text-preset'
import linkResolver from '~/lib/link-resolver'
import { STORYBLOK_TYPES } from '~/lib/storyblok-types'

import { BigCloseIcon } from '~/components/UI/Icons'
import { UrlParamsProps } from '~/components/UI/SearchContent'

import { useStyle } from '~/providers/StyleProvider'

import useLocale from '~/hooks/useLocale'
import useOnKeyDown from '~/hooks/useOnKeyDown'

import { stringIsNotEmpty } from '~/utils/check-empty-string'
import { getQueryParams } from '~/utils/get-query-params'

import { FORM } from '~/data/dictionary'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export type SearchBarProps = {
  className?: string
  inputClassName?: string
  hasReset?: boolean
  hasAutocomplete?: boolean
  autocompleteValue?: string
  suggestedValue?: string
  isSearching?: boolean
  isFetching?: boolean
  inputValue?: string
}

function SearchBar({
  className,
  inputClassName,
  hasAutocomplete,
  autocompleteValue,
  suggestedValue,
  hasReset,
  isSearching,
  isFetching,
  inputValue,
}: SearchBarProps) {
  const t = useTranslate()
  const inputRef = useRef(null)
  const router = useRouter()
  const locale = useLocale()
  const [isFocused, setIsFocused] = useState(false)
  const [isFullySuggested, setIsFullySuggested] = useState(false)
  const [shouldDisplayPlaceholder, setShouldDisplayPlaceHolder] = useState(true)
  const inputStyle = useStyle({
    textPreset: GlobalTextPreset.Input30_56SangBleu,
    color: GlobalThemeColors.Black,
  })

  const onInputChange = useCallback(
    (e) => {
      const q = e.currentTarget.textContent

      router.push(
        {
          pathname: linkResolver(
            { content: { component: STORYBLOK_TYPES.SEARCH } },
            locale,
          ),
          query: {
            q,
          },
        },
        null,
        {
          shallow: true,
          locale,
        },
      )
    },
    [router, locale],
  )

  const clearSearch = useCallback(() => {
    router.push(
      {
        pathname: linkResolver(
          { content: { component: STORYBLOK_TYPES.SEARCH } },
          locale,
        ),
      },
      null,
      {
        shallow: true,
        locale,
      },
    )
    inputRef.current.textContent = ''
    setIsFullySuggested(false)
  }, [])

  const params = useMemo<UrlParamsProps>(() => getQueryParams(), [router])
  const { q } = params
  const query = q?.toString()

  useEffect(() => {
    if (query) {
      setShouldDisplayPlaceHolder(false)
      inputRef.current.textContent = query?.toString()?.replaceAll('+', ' ')
    } else {
      setShouldDisplayPlaceHolder(true)
    }
  }, [])

  useEffect(() => {
    isFocused || isSearching
      ? setShouldDisplayPlaceHolder(false)
      : setShouldDisplayPlaceHolder(true)
  }, [isFocused, isSearching])

  const focusAtEnd = () => {
    // This function is used to focus at the end of the input
    // Because its a contenteditable element, the cursor has not the good position when overriding the value

    const input = inputRef.current
    const range = document.createRange()
    const selection = window.getSelection()
    const lastChildNode = input.childNodes[input.childNodes.length - 1]

    range.setStart(lastChildNode, lastChildNode.length)
    range.collapse(true)

    selection.removeAllRanges()
    selection.addRange(range)
  }

  const onClickReset = (e) => {
    e.preventDefault()
    clearSearch()
  }

  const onKeyDown = (e) => {
    if (e.keyCode === 13 || e.keyCode === 39) {
      e.preventDefault()
    }
  }

  const onSuggestion = () => {
    if (hasAutocomplete && suggestedValue) {
      inputRef.current.textContent = suggestedValue
      onInputChange({ currentTarget: inputRef.current })
      focusAtEnd()
      setIsFullySuggested(true)
    }
  }

  const onInput = (e) => {
    onInputChange(e)
    setIsFullySuggested(false)
  }

  useOnKeyDown(27, () => {
    if (stringIsNotEmpty(query)) clearSearch()
  })

  useOnKeyDown(13, onSuggestion)
  useOnKeyDown(39, onSuggestion)

  return (
    <div
      className={cx(css.SearchBar, className, {
        hasReset: hasReset && isSearching,
        hasAutocomplete: hasAutocomplete,
      })}>
      <span
        className={cx(css.searchInput, inputStyle, inputClassName, {
          isWaitingForFocus: !isSearching && !isFocused,
          hasNoAutocompleteValue: hasAutocomplete && !autocompleteValue,
          hasNoInput: !stringIsNotEmpty(query),
        })}
        role="textbox"
        spellCheck={false}
        ref={inputRef}
        onInput={onInput}
        onKeyDown={onKeyDown}
        onFocus={() => {
          setIsFocused(true)
        }}
        onBlur={() => {
          setIsFocused(false)
        }}
        contentEditable
      />

      <h1
        className={cx(css.fakePlaceholder, inputStyle, inputClassName, {
          shouldDisplayPlaceholder,
        })}>
        {t(FORM.SEARCH)}
      </h1>

      {hasAutocomplete &&
        autocompleteValue &&
        !isFetching &&
        !isFullySuggested &&
        stringIsNotEmpty(query) && (
          <span className={cx(css.autocompleteValue, inputStyle)}>
            {autocompleteValue}
          </span>
        )}
      {hasReset && isSearching && (
        <button
          aria-label={t(FORM.RESET)}
          onClick={(e) => {
            onClickReset && onClickReset(e)
          }}
          className={css.resetCta}>
          <BigCloseIcon className={css.closeIcon} />
        </button>
      )}
    </div>
  )
}

SearchBar.defaultProps = {
  hasReset: false,
  hasAutocomplete: false,
  isSearching: false,
}

export default SearchBar
